import { useMemo, useState, type ReactElement } from 'react';
import { PAGE_LIMIT_MAX } from '@gr/core/paging';
import { PostSort, type PostSummaryDto } from '@gr/core/content/posts/dto';
import { zonedParts } from '@gr/core/time/zoned-time';
import type { OperationResult } from '@gr/utils/operation-result';
import { useConfig, useServices, useT } from '../../../app/di';
import { useResource } from '../../../shared/hooks';
import { Alert } from '../../../ui-kit/Alert/Alert';
import { Button, IconButton } from '../../../ui-kit/Button/Button';
import { SegmentedControl } from '../../../ui-kit/Choice/Choice';
import { BareSelect } from '../../../ui-kit/Field/Field';
import { Loading } from '../../../ui-kit/Loading/Loading';
import { PageHeader } from '../../../ui-kit/PageHeader/PageHeader';
import { Tabs } from '../../../ui-kit/Tabs/Tabs';
import { useChannelOptions } from '../channels';
import { useBlog } from '../../accounts/workspaces';
import { buildDays, CalendarSource, CalendarView, findConflicts, groupByDay, groupPostsByDay, periodLabel, periodRange, shiftAnchor, type CalendarDay } from './model/calendar';
import { PostCalendar } from './PostCalendar/PostCalendar';
import { PublicationCalendar } from './PublicationCalendar/PublicationCalendar';

interface PeriodProps {
  view: CalendarView;
  days: readonly CalendarDay[];
  today: CalendarDay;
  month: number;
  from: string;
  to: string;
}

// SCH-UC-7, SCH-RULE-4
export function CalendarPage(): ReactElement {
  const { api } = useServices();
  const { t, locale } = useT();
  const { blog } = useBlog();
  const today = useMemo<CalendarDay>(() => zonedParts(new Date(), blog.timezone), [blog.timezone]);
  const [view, setView] = useState<CalendarView>(CalendarView.Week);
  const [anchor, setAnchor] = useState<CalendarDay>(today);
  const [source, setSource] = useState<CalendarSource>(CalendarSource.Publications);
  const [channelId, setChannelId] = useState('');
  const [postsBy, setPostsBy] = useState<PostSort>(PostSort.Created);
  const channels = useResource(() => api.channels.list(blog.id), [api, blog.id]);
  // CHN-UC-6: платформа канала — в подписи варианта.
  const channelOptions = useChannelOptions(channels.data ?? []);
  const days = useMemo(() => buildDays(anchor, view), [anchor, view]);
  // Записи грузятся ровно за дни на экране: неделю или сетку месяца; листание — следующий период.
  const { from, to } = periodRange(days, blog.timezone);
  const period: PeriodProps = { view, days, today, month: anchor.month, from, to };

  return (
    <div className="u-stack-lg">
      <PageHeader
        title={t('scheduling.calendar.title')}
        subtitle={
          <span data-testid="calendar-period" aria-live="polite">
            {periodLabel(view, days, anchor, locale)}
          </span>
        }
      />
      <div className="u-row">
        <IconButton icon="left" label={t('scheduling.calendar.prev')} onClick={() => setAnchor(shiftAnchor(anchor, view, -1, today))} />
        <Button size="sm" onClick={() => setAnchor(today)}>
          {t('scheduling.calendar.today')}
        </Button>
        <IconButton icon="right" label={t('scheduling.calendar.next')} onClick={() => setAnchor(shiftAnchor(anchor, view, 1, today))} />
      </div>
      <section className="u-row" aria-label={t('scheduling.calendar.filter')}>
        <SegmentedControl<CalendarSource>
          label={t('scheduling.calendar.source')}
          options={[
            { value: CalendarSource.Publications, label: t('scheduling.calendar.publications') },
            { value: CalendarSource.Posts, label: t('scheduling.calendar.posts') },
          ]}
          value={source}
          onChange={setSource}
        />
        {source === CalendarSource.Publications ? (
          <BareSelect
            label={t('scheduling.calendar.channel')}
            value={channelId}
            onChange={(e) => setChannelId(e.target.value)}
            options={[{ value: '', label: t('scheduling.calendar.allChannels') }, ...channelOptions]}
          />
        ) : (
          <SegmentedControl<PostSort>
            label={t('scheduling.calendar.postsBy')}
            options={[
              { value: PostSort.Created, label: t('scheduling.calendar.byCreated') },
              { value: PostSort.Updated, label: t('scheduling.calendar.byUpdated') },
            ]}
            value={postsBy}
            onChange={setPostsBy}
          />
        )}
      </section>
      <Tabs<CalendarView>
        label={t('scheduling.calendar.view')}
        items={[
          { key: CalendarView.Week, label: t('scheduling.calendar.week') },
          { key: CalendarView.Month, label: t('scheduling.calendar.month') },
        ]}
        active={view}
        onChange={setView}
      />
      {source === CalendarSource.Publications ? <PublicationsInPeriod {...period} channelId={channelId} /> : <PostsInPeriod {...period} by={postsBy} />}
    </div>
  );
}

/** Публикации периода (по умолчанию): запланированные и записанные выходы. */
function PublicationsInPeriod({ from, to, channelId, ...grid }: PeriodProps & { channelId: string }): ReactElement {
  const { api } = useServices();
  const { ui } = useConfig();
  const { t, te } = useT();
  const { blog } = useBlog();
  const publications = useResource(() => api.scheduling.calendar(blog.id, { from, to, channelId: channelId || undefined }), [api, blog.id, from, to, channelId]);
  const byDay = useMemo(() => groupByDay(publications.data ?? [], blog.timezone), [publications.data, blog.timezone]);
  const conflicts = useMemo(() => findConflicts(publications.data ?? []), [publications.data]);
  return (
    <>
      {conflicts.size > 0 ? <Alert tone="warning">{t('scheduling.calendar.conflict')}</Alert> : null}
      {publications.error ? <Alert tone="danger">{te(publications.error)}</Alert> : null}
      {publications.loading && !publications.data ? (
        <Loading label={t('common.loading')} />
      ) : (
        <PublicationCalendar {...grid} blogId={blog.id} timezone={blog.timezone} byDay={byDay} conflicts={conflicts} monthCellLimit={ui.calendar.monthCellLimit} />
      )}
      {!publications.loading && publications.data?.length === 0 ? <p className="u-muted">{t('scheduling.calendar.empty')}</p> : null}
    </>
  );
}

/** Посты периода по дате создания или изменения — все страницы списка за период. */
function PostsInPeriod({ from, to, by, ...grid }: PeriodProps & { by: PostSort }): ReactElement {
  const { api } = useServices();
  const { ui } = useConfig();
  const { t, te } = useT();
  const { blog } = useBlog();
  const posts = useResource(async (): Promise<OperationResult<PostSummaryDto[]>> => {
    const all: PostSummaryDto[] = [];
    for (let offset = 0; ; offset += PAGE_LIMIT_MAX) {
      const page = await api.posts.list(blog.id, { sort: by, from, to, limit: PAGE_LIMIT_MAX, offset });
      if (!page.success) return page;
      all.push(...page.data);
      if (page.data.length < PAGE_LIMIT_MAX) return { ...page, data: all };
    }
  }, [api, blog.id, by, from, to]);
  const statuses = useResource(() => api.posts.statuses(blog.id), [api, blog.id]);
  const statusNames = useMemo(() => new Map((statuses.data ?? []).map((s) => [s.id, s.name])), [statuses.data]);
  const byDay = useMemo(() => groupPostsByDay(posts.data ?? [], by, blog.timezone), [posts.data, by, blog.timezone]);
  return (
    <>
      {posts.error ? <Alert tone="danger">{te(posts.error)}</Alert> : null}
      {posts.loading && !posts.data ? (
        <Loading label={t('common.loading')} />
      ) : (
        <PostCalendar {...grid} blogId={blog.id} timezone={blog.timezone} byDay={byDay} by={by} statusNames={statusNames} monthCellLimit={ui.calendar.monthCellLimit} />
      )}
      {!posts.loading && posts.data?.length === 0 ? <p className="u-muted">{t('scheduling.calendar.noPosts')}</p> : null}
    </>
  );
}
